import { create } from 'zustand';
import { isAxiosError } from 'axios';
import { useCases } from '../../infrastructure/di/container';
import { getErrorMessage } from '../../infrastructure/http/common';
import { CrawlerEntity, CrawlerImportPayload } from '../../domain/entities/Crawler';

const toErrorSlug = (e: unknown): string => {
  if (isAxiosError(e)) return getErrorMessage(e);
  if (e instanceof Error && e.message) return e.message;
  return 'unknown-error';
};

interface CrawlersState {
  items: CrawlerEntity[];
  isLoading: boolean;
  isSaving: boolean;
  error?: string;
  load: () => Promise<void>;
  add: (phone: string) => Promise<string>;
  importFromSession: (payload: Partial<CrawlerImportPayload>) => Promise<string>;
  requestCode: (uuid: string) => Promise<void>;
  confirmCode: (uuid: string, code: string, password: string) => Promise<void>;
  release: (uuid: string) => Promise<void>;
}

export const useCrawlersStore = create<CrawlersState>((set, get) => {
  const mutate = async <T>(action: () => Promise<T>): Promise<T> => {
    set({ isSaving: true });
    try {
      const result = await action();
      await get().load();
      return result;
    } catch (e) {
      throw new Error(toErrorSlug(e));
    } finally {
      set({ isSaving: false });
    }
  };

  return {
    items: [],
    isLoading: false,
    isSaving: false,
    async load() {
      set({ isLoading: true, error: undefined });
      try {
        set({ items: await useCases.listCrawlers().execute(), isLoading: false });
      } catch (e) {
        set({ isLoading: false, error: toErrorSlug(e) });
      }
    },
    add(phone) {
      return mutate(() => useCases.addCrawler().execute(phone));
    },
    importFromSession(payload) {
      return mutate(() => useCases.importCrawler().execute(payload));
    },
    requestCode(uuid) {
      return mutate(() => useCases.requestCrawlerCode().execute(uuid));
    },
    confirmCode(uuid, code, password) {
      return mutate(() => useCases.confirmCrawlerCode().execute(uuid, code, password));
    },
    release(uuid) {
      return mutate(() => useCases.releaseCrawler().execute(uuid));
    },
  };
});
